import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { visuallyHidden } from '@mui/utils';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import { Edit2Icon } from 'lucide-react';
import { useState } from 'react';
import EditQuestionModal from './EditQuestion';
import { useQuestionsContext } from '../../../context/QuestionsContext';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    {
        id: 'edit',
        numeric: false,
        disablePadding: true,
        label: '',
    },
    {
        id: 'needsReview',
        numeric: false,
        disablePadding: false,
        label: 'Needs Review',
    },
    {
        id: 'text',
        numeric: false,
        disablePadding: true,
        label: 'Question Text',
    },
    {
        id: 'correctAnswers',
        numeric: false,
        disablePadding: false,
        label: 'Correct Answers List',
    },
    {
        id: 'sourceType',
        numeric: true,
        disablePadding: false,
        label: 'Source Type',
    },
    {
        id: 'sourceTitle',
        numeric: true,
        disablePadding: false,
        label: 'Title',
    },
    {
        id: 'chapter',
        numeric: false,
        disablePadding: false,
        label: 'Chapter',
    },
    {
        id: 'difficulty',
        numeric: false,
        disablePadding: false,
        label: 'Difficulty',
    },
];

function QuestionDataTableHead(props) {
    const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
    } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={
                            headCell.numeric
                                ? 'right'
                                : 'left'
                        }
                        padding={
                            headCell.disablePadding
                                ? 'none'
                                : 'normal'
                        }
                        sortDirection={
                            orderBy === headCell.id
                                ? order
                                : false
                        }
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={
                                orderBy === headCell.id
                                    ? order
                                    : 'asc'
                            }
                            onClick={createSortHandler(
                                headCell.id
                            )}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box
                                    component='span'
                                    sx={visuallyHidden}
                                >
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

QuestionDataTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

QuestionDataTableHead.defaultProps = {
    onSelectAllClick: () => {},
};

function QuestionDataTableToolbar(props) {
    const {
        numSelected,
        onToggleFilters,
        showFilters,
        hasActiveFilters,
    } = props;
    return (
        <Toolbar
            sx={[
                {
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                },
                numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(
                            theme.palette.primary.main,
                            theme.palette.action
                                .activatedOpacity
                        ),
                },
            ]}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color='inherit'
                    variant='subtitle1'
                    component='div'
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography>
                    Filter or Select Questions for Review
                </Typography>
            )}
            {numSelected > 0 ? (
                <Tooltip title='Delete'>
                    <IconButton>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title='Filter list'>
                    <IconButton
                        onClick={onToggleFilters}
                        color={
                            hasActiveFilters
                                ? 'primary'
                                : 'default'
                        }
                    >
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
}

QuestionDataTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onToggleFilters: PropTypes.func.isRequired,
    showFilters: PropTypes.bool.isRequired,
    hasActiveFilters: PropTypes.bool.isRequired,
};

function FilterPanel({
    filters,
    onFilterChange,
    onClearFilters,
    sources,
}) {
    return (
        <Box
            sx={{
                p: 2,
                bgcolor: 'background.default',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <FormControl
                    sx={{ minWidth: 200 }}
                    size='small'
                >
                    <InputLabel>Source</InputLabel>
                    <Select
                        value={filters.sourceId}
                        label='Source'
                        onChange={(e) =>
                            onFilterChange(
                                'sourceId',
                                e.target.value
                            )
                        }
                    >
                        <MenuItem value=''>
                            All Sources
                        </MenuItem>
                        {sources.map((source) => (
                            <MenuItem
                                key={source.id}
                                value={source.id}
                            >
                                {`${source.title} (${source.type})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl
                    sx={{ minWidth: 200 }}
                    size='small'
                >
                    <InputLabel>Difficulty</InputLabel>
                    <Select
                        value={filters.difficulty}
                        label='Difficulty'
                        onChange={(e) =>
                            onFilterChange(
                                'difficulty',
                                e.target.value
                            )
                        }
                    >
                        <MenuItem value=''>
                            All Difficulties
                        </MenuItem>
                        <MenuItem value='Easy'>
                            Easy
                        </MenuItem>
                        <MenuItem value='Medium'>
                            Medium
                        </MenuItem>
                        <MenuItem value='Hard'>
                            Hard
                        </MenuItem>
                        <MenuItem value='Very Hard'>
                            Very Hard
                        </MenuItem>
                        <MenuItem value='Impossible'>
                            Impossible
                        </MenuItem>
                    </Select>
                </FormControl>
                
                <FormControl sx={{ minWidth: 200 }} size='small'>
                    <InputLabel>Question Type</InputLabel>
                    <Select
                        value={filters.questionType}
                        label='Question Type'
                        onChange={(e) => onFilterChange('questionType', e.target.value)}
                    >
                        <MenuItem value=''>All Types</MenuItem>
                        <MenuItem value='TextInput'>TextInput</MenuItem>
                        <MenuItem value='MultipleChoice'>MultipleChoice</MenuItem>
                    </Select>
                </FormControl>

                <FormControl
                    sx={{ minWidth: 200 }}
                    size='small'
                >
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={filters.isActive}
                        label='Status'
                        onChange={(e) =>
                            onFilterChange(
                                'isActive',
                                e.target.value
                            )
                        }
                    >
                        <MenuItem value=''>All</MenuItem>
                        <MenuItem value='active'>
                            Active
                        </MenuItem>
                        <MenuItem value='inactive'>
                            Inactive
                        </MenuItem>
                    </Select>
                </FormControl>

                <FormControl
                    sx={{ minWidth: 200 }}
                    size='small'
                >
                    <InputLabel>Review Status</InputLabel>
                    <Select
                        value={filters.needsReview}
                        label='Review Status'
                        onChange={(e) =>
                            onFilterChange(
                                'needsReview',
                                e.target.value
                            )
                        }
                    >
                        <MenuItem value=''>All</MenuItem>
                        <MenuItem value='needs_review'>
                            Needs Review
                        </MenuItem>
                        <MenuItem value='reviewed'>
                            Reviewed
                        </MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant='outlined'
                    size='small'
                    onClick={onClearFilters}
                    startIcon={<CloseIcon />}
                >
                    Clear Filters
                </Button>
            </Box>
        </Box>
    );
}

export default function AdminQuestionDataTable({ user }) {

    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('text');
    const [selected, setSelected] = React.useState([]);
    const { deleteQuestion } = useQuestionsContext();
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [page, setPage] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] =
        React.useState(10);
    const [showFilters, setShowFilters] =
        React.useState(false);
    const [filters, setFilters] = React.useState({
        sourceId: '',
        difficulty: '',
        isActive: '',
        needsReview: '',
        questionType: '',
    });
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());

    const {
        questions = [],
        sources = [],
        loading,
        error,
    } = useQuestionsContext();

    // Apply filters
    const filteredQuestions = React.useMemo(() => {
        return questions.filter((question) => {
            if (
                filters.sourceId &&
                question.source?.sourceId !==
                    filters.sourceId
            ) {
                return false;
            }
            if (filters.difficulty && question.difficulty !== filters.difficulty) {
                return false;
            }
            if (filters.isActive) {
                if (
                    filters.isActive === 'active' &&
                    !question.isActive
                )
                    return false;
                if (
                    filters.isActive === 'inactive' &&
                    question.isActive
                )
                    return false;
            }
            if (filters.questionType && question.questionType !== filters.questionType) {
                return false;
            }
            if (filters.needsReview) {
                if (
                    filters.needsReview ===
                        'needs_review' &&
                    !question.needsReview
                )
                    return false;
                if (
                    filters.needsReview === 'reviewed' &&
                    question.needsReview
                )
                    return false;
            }

            return true;
        });
    }, [questions, filters]);

    const hasActiveFilters = Object.values(filters).some(
        (value) => value !== ''
    );

    const emptyRows =
        page > 0
            ? Math.max(
                  0,
                  (1 + page) * rowsPerPage -
                      filteredQuestions.length
              )
            : 0;

    const visibleRows = React.useMemo(
        () =>
            [...filteredQuestions]
                .sort(getComparator(order, orderBy))
                .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                ),
        [
            order,
            orderBy,
            page,
            rowsPerPage,
            filteredQuestions,
        ]
    );

    const handleRequestSort = (event, property) => {
        const isAsc =
            orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const [selectedQuestion, setSelectedQuestion] =
        useState(null);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterName]: value,
        }));
        setPage(0); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({
            sourceId: '',
            difficulty: '',
            isActive: '',
            needsReview: '',
            questionType: '',
        });
        setPage(0);
    };

    const toggleExpanded = (questionId) => {
        setExpandedQuestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(questionId)) {
                newSet.delete(questionId);
            } else {
                newSet.add(questionId);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <Typography>Loading questions...</Typography>
        );
    }

    if (error) {
        return (
            <Typography color='error'>
                Error loading questions: {error}
            </Typography>
        );
    }

    return (
        // TODO: Wrapped the table row and the delete dialog template in a fragment. Learn more about fragments and best practices etc
        <>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>
                    Are you sure you want to delete this question?
                </DialogTitle>

                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        No
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            await deleteQuestion(deleteTarget.questionId);
                            setConfirmOpen(false);
                            setDeleteTarget(null);
                        }}
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <QuestionDataTableToolbar
                        numSelected={selected.length}
                        onToggleFilters={() =>
                            setShowFilters(!showFilters)
                        }
                        showFilters={showFilters}
                        hasActiveFilters={hasActiveFilters}
                    />
                    <Collapse in={showFilters}>
                        <FilterPanel
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={handleClearFilters}
                            sources={sources}
                        />
                    </Collapse>
                    <Box
                        sx={{
                            display: {
                                xs: 'none',
                                md: 'block',
                            },
                        }}
                    >
                        <TableContainer
                            component={Paper}
                            sx={{ maxWidth: '100%' }}
                        >
                            <Table
                                sx={{ minWidth: 750 }}
                                aria-labelledby='tableTitle'
                                size={
                                    dense ? 'small' : 'medium'
                                }
                            >
                                <QuestionDataTableHead
                                    numSelected={
                                        selected.length
                                    }
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={
                                        handleRequestSort
                                    }
                                    rowCount={
                                        filteredQuestions.length
                                    }
                                />
                                <TableBody>
                                    {visibleRows.map(
                                        (row, index) => {
                                            const isItemSelected =
                                                selected.includes(
                                                    row.questionId
                                                );

                                            const labelId = `question-table-checkbox-${index}`;

                                            return (
                                                <TableRow
                                                    tabIndex={
                                                        -1
                                                    }
                                                    key={
                                                        row.questionId
                                                    }
                                                >
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton onClick={() => setSelectedQuestion(row)}>
                                                            <Edit2Icon />
                                                        </IconButton>

                                                        <br />

                                                        <IconButton
                                                            onClick={() => {
                                                                setDeleteTarget(row);
                                                                setConfirmOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon color="error" />
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell
                                                        padding='none'
                                                        margin='0'
                                                    >
                                                        {row.needsReview ? (
                                                            <Chip
                                                                label='Yes'
                                                                color='error'
                                                                size='small'
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label='No'
                                                                color='info'
                                                                size='small'
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        component='th'
                                                        id={
                                                            labelId
                                                        }
                                                        scope='row'
                                                        padding='none'
                                                        sx={{
                                                            maxWidth: 300,
                                                        }}
                                                    >
                                                        <Tooltip title={row.text} arrow placement="top">
                                                            <Box
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitBoxOrient: 'vertical',
                                                                    WebkitLineClamp: 3, // <-- Show up to 3 lines
                                                                }}
                                                            >
                                                                {row.text}
                                                            </Box>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.correctAnswers.join(
                                                            ', '
                                                        )}
                                                    </TableCell>
                                                    <TableCell align='right'>
                                                        {row
                                                            .source
                                                            .type ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell align='right'>
                                                        {row
                                                            .source
                                                            .title ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.chapter ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.difficulty ? (
                                                            <Chip
                                                                label={
                                                                    row.difficulty
                                                                }
                                                                color={
                                                                    row.difficulty ===
                                                                    'easy'
                                                                        ? 'success'
                                                                        : row.difficulty ===
                                                                        'medium'
                                                                        ? 'warning'
                                                                        : 'error'
                                                                }
                                                                size='small'
                                                            />
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                    )}
                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height:
                                                    (dense
                                                        ? 33
                                                        : 53) *
                                                    emptyRows,
                                            }}
                                        >
                                            <TableCell
                                                colSpan={11}
                                            />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Box
                        sx={{
                            display: {
                                xs: 'block',
                                md: 'none',
                            },
                        }}
                    >
                        {visibleRows.map((q) => (
                            <Paper key={q.questionId} sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {expandedQuestions.has(q.questionId) || q.text.length < 150
                                        ? q.text
                                        : `${q.text.substring(0, 150)}...`}
                                    {q.text.length > 150 && (
                                        <Button
                                            size="small"
                                            onClick={() => toggleExpanded(q.questionId)}
                                            sx={{ ml: 1, textTransform: 'none', minWidth: 'auto', p: 0 }}
                                        >
                                            {expandedQuestions.has(q.questionId) ? 'Show less' : 'Show more'}
                                        </Button>
                                    )}
                                </Typography>

                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {q.source?.title || 'Unknown'}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        mt: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Chip label={q.difficulty} size="small" />
                                    {q.needsReview && (
                                        <Chip label="Needs Review" color="warning" size="small" />
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <IconButton onClick={() => setSelectedQuestion(q)}>
                                        <Edit2Icon />
                                    </IconButton>

                                    <IconButton
                                        onClick={() => {
                                            setDeleteTarget(q);
                                            setConfirmOpen(true);
                                        }}
                                    >
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>

                    <EditQuestionModal
                        open={!!selectedQuestion}
                        question={selectedQuestion}
                        onClose={() => setSelectedQuestion(null)}
                        user={user}
                    />
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component='div'
                        count={filteredQuestions.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={
                            handleChangeRowsPerPage
                        }
                    />
                </Paper>
            </Box>
        </>
    ); 
}